
import {
  defineComponent,
  onMounted,
  reactive,
  ref,
  nextTick,
  computed,
} from "vue";
import XAtable from "@/components/XATable.vue";
import { sysUserQueryList, sysUserAdd, sysUserUpdate, sysUserResetPassword, roleQueryPageList } from "@/api/api";
import XDialog from "@/components/XDialog.vue";
import type { FormInstance } from "ant-design-vue";
import { hasPermission } from "@/utils/permission";
import { message } from "ant-design-vue";
import globalConfig from "@/utils/config";

interface SearchFormState {
  account: string;
  name: string;
  roleId: number | undefined;
}

interface FormState {
  id: number | string;
  account: string;
  name: string;
  password: string;
  status: number;
  remark: string;
  roleId: number;
}

export default {
  components: {
    XAtable,
    XDialog,
  },
  setup() {
    const searchFormState = reactive<SearchFormState>({
      account: "",
      name: "",
      roleId: undefined,
    });
    const formState = reactive<FormState>({
      id: "",
      account: "",
      name: "",
      password: "",
      status: 1,
      remark: "",
      roleId: 2,
    });
    const isAdd = ref(1);
    const dislogVisible = ref(false);
    const addLoading = ref(false);
    const pageNo = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const loading = ref(false);
    const tableData = ref([]);
    const formRef = ref<FormInstance>();
    const roleList = ref<any[]>([]);
    const roleListLoading = ref(false);
    const resetPasswordLoading = ref(false);
    const newPassword = ref("");

    // 基础列配置
    const baseColumns = [
      {
        title: "ID",
        key: "id",
        dataIndex: "id",
      },
      {
        title: "用户账号",
        key: "account",
        dataIndex: "account",
      },
      {
        title: "用户名",
        key: "name",
        dataIndex: "name",
      },
      {
        title: "角色名称",
        key: "roleName",
        dataIndex: "roleName",
      },
      {
        title: "状态",
        key: "status",
        dataIndex: "status",
        slots: {
          customRender: "status",
        },
      },
      {
        title: "创建时间",
        key: "createTime",
        dataIndex: "createTime",
      },
    ];

    // 操作列配置
    const operationColumn = {
      title: "操作",
      key: "control",
      dataIndex: "control",
      slots: {
        customRender: "control",
      },
    };

    // 动态计算是否显示操作列
    const hasAnyOperationPermission = computed(() => {
      // 检查是否有任何操作权限
      return (
        hasPermission("sysUser.list.update") ||
        hasPermission("sysWork.list.read")
      );
    });

    // 动态列配置
    const columns = computed(() => {
      const cols = [...baseColumns];
      // 如果有任何操作权限，则显示操作列
      if (hasAnyOperationPermission.value) {
        cols.push(operationColumn);
      }
      return cols;
    });

    const search = () => {
      pageNo.value = 1;
      getData();
    };
    const resetForm = () => {
      searchFormState.account = "";
      searchFormState.name = "";
      searchFormState.roleId = undefined;
      pageNo.value = 1;
      getData();
    };
    const getData = () => {
      loading.value = true;
      var parms: any = {
        account: searchFormState.account,
        name: searchFormState.name,
        pageNo: pageNo.value,
        pageSize: pageSize.value,
      };
      if (searchFormState.roleId !== undefined && searchFormState.roleId !== null) {
        parms.roleId = searchFormState.roleId;
      }
      sysUserQueryList(parms).then((res) => {
        loading.value = false;
        total.value = res.data.total;
        tableData.value = res.data.records;
      });
    };
    const pageNoChange = (val) => {
      pageNo.value = val;
      getData();
    };
    const onShowSizeChange = (current, size) => {
      pageSize.value = size;
      getData();
    };
    const addClick = async () => {
      isAdd.value = 1;
      formState.id = "";
      formState.account = "";
      formState.name = "";
      formState.password = "";
      formState.status = 1;
      formState.remark = "";
      formState.roleId = 2;

      // 重置表单验证状态
      await nextTick();
      formRef.value?.resetFields();
      dislogVisible.value = true;
    };
    const editClick = async (row: any) => {
      isAdd.value = 0;
      newPassword.value = "";
      // 先打开弹窗
      dislogVisible.value = true;

      // 等待弹窗渲染完成后再设置值
      await nextTick();

      // 明确赋值每个字段，确保数据正确回显
      formState.id = row.id || "";
      formState.account = row.account || "";
      formState.name = row.name || "";
      formState.password = ""; // 编辑时不显示密码
      formState.status = row.status !== undefined ? row.status : 1;
      formState.remark = row.remark || "";
      formState.roleId = row.roleId || 2;

      // 清除表单验证状态，但不重置字段值
      await nextTick();
      formRef.value?.clearValidate();
    };
    const handleCancel = async () => {
      dislogVisible.value = false;
      addLoading.value = false;
      newPassword.value = "";
      await nextTick();
      formRef.value?.resetFields();
    };
    const confirm = async () => {
      try {
        const values = await formRef.value.validateFields();
        addLoading.value = true;

        if (isAdd.value === 1) {
          // 新增时不传id字段
          const { id, ...addData } = formState;
          sysUserAdd(addData).then((res) => {
            addLoading.value = false;
            if (res.code === "200") {
              dislogVisible.value = false;
              getData();
            }
          });
        } else {
          sysUserUpdate(formState).then((res) => {
            addLoading.value = false;
            if (res.code === "200") {
              dislogVisible.value = false;
              getData();
            }
          });
        }
      } catch (errorInfo) {
        console.log("Failed:", errorInfo);
      }
    };

    // 复制推广链接
    const copyCode = (row: any) => {
      if (!row.code) {
        message.warning("该代理没有推广码");
        return;
      }

      // 拼接完整的推广链接
      const promotionUrl = `${(globalConfig as any).promotionDomain || 'https://www.bm2500.com'}?code=${row.code}`;

      // 使用 Clipboard API 复制
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(promotionUrl)
          .then(() => {
            message.success(`已复制推广链接: ${promotionUrl}`);
          })
          .catch(() => {
            // 如果 Clipboard API 失败，使用备用方案
            fallbackCopy(promotionUrl);
          });
      } else {
        // 浏览器不支持 Clipboard API，使用备用方案
        fallbackCopy(promotionUrl);
      }
    };

    // 备用复制方案
    const fallbackCopy = (text: string) => {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        message.success(`已复制: ${text}`);
      } catch (err) {
        message.error("复制失败，请手动复制");
      }
      document.body.removeChild(textarea);
    };

    const copyNewPassword = () => {
      fallbackCopy(newPassword.value);
    };

    const handleResetPassword = () => {
      resetPasswordLoading.value = true;
      sysUserResetPassword({ id: formState.id }).then((res) => {
        resetPasswordLoading.value = false;
        if (res.code === "200") {
          newPassword.value = res.data?.password || "";
          message.success("密码重置成功");
        }
      }).catch(() => {
        resetPasswordLoading.value = false;
      });
    };

    // 限制用户账号输入：只允许数字和英文字母
    const limitAccountInput = (event: any, type: 'search' | 'form') => {
      const value = event.target.value;
      // 只保留数字和英文字母（大小写）
      const filteredValue = value.replace(/[^a-zA-Z0-9]/g, '');
      if (type === 'search') {
        searchFormState.account = filteredValue;
      } else {
        formState.account = filteredValue;
      }
    };

    // 获取角色列表
    const getRoleList = () => {
      roleListLoading.value = true;
      roleQueryPageList({
        pageNo: 1,
        pageSize: 100, // 设置较大的 pageSize 获取所有角色
      }).then((res) => {
        roleListLoading.value = false;
        if (res.code === "200") {
          roleList.value = res.data.records || [];
        }
      }).catch(() => {
        roleListLoading.value = false;
      });
    };

    onMounted(() => {
      getData();
      getRoleList();
    });
    return {
      isAdd,
      formRef,
      dislogVisible,
      addClick,
      confirm,
      handleCancel,
      searchFormState,
      formState,
      addLoading,
      columns,
      tableData,
      pageNo,
      total,
      loading,
      search,
      resetForm,
      pageNoChange,
      onShowSizeChange,
      editClick,
      copyCode,
      roleList,
      roleListLoading,
      limitAccountInput,
      resetPasswordLoading,
      handleResetPassword,
      newPassword,
      copyNewPassword,
    };
  },
};
